import React, { useState, useMemo, useCallback } from 'react';
import {
    Box,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Typography,
    Stack,
    Divider,
    Checkbox,
    ListItemText,
    ListItemIcon,
    ListSubheader,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import SaveIcon from '@mui/icons-material/Save';
import FilterListIcon from '@mui/icons-material/FilterList';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import Menu from '@mui/material/Menu';
import MenuItemMUI from '@mui/material/MenuItem';

interface Player {
    id: number;
    name: string;
    yearBorn: number;
    team: string;
    position: string;
    jerseyNumber: number;
    // Add other relevant player properties
}

const initialPlayers: Player[] = [
    { id: 1, name: 'Lionel Messi', yearBorn: 1987, team: 'Inter Miami CF', position: 'Forward', jerseyNumber: 10 },
    { id: 2, name: 'Cristiano Ronaldo', yearBorn: 1985, team: 'Al Nassr FC', position: 'Forward', jerseyNumber: 7 },
    { id: 3, name: 'Kylian Mbappé', yearBorn: 1998, team: 'Paris Saint-Germain', position: 'Forward', jerseyNumber: 7 },
    { id: 4, name: 'Erling Haaland', yearBorn: 2000, team: 'Manchester City', position: 'Forward', jerseyNumber: 9 },
    { id: 5, name: 'Kevin De Bruyne', yearBorn: 1991, team: 'Manchester City', position: 'Midfielder', jerseyNumber: 17 },
    { id: 6, name: 'Virgil van Dijk', yearBorn: 1991, team: 'Liverpool FC', position: 'Defender', jerseyNumber: 4 },
    { id: 7, name: 'Mohamed Salah', yearBorn: 1992, team: 'Liverpool FC', position: 'Forward', jerseyNumber: 11 },
    { id: 8, name: 'Neymar Jr.', yearBorn: 1992, team: 'Al Hilal SFC', position: 'Forward', jerseyNumber: 10 },
    { id: 9, name: 'Harry Kane', yearBorn: 1993, team: 'FC Bayern Munich', position: 'Forward', jerseyNumber: 9 },
    { id: 10, name: 'Luka Modrić', yearBorn: 1985, team: 'Real Madrid CF', position: 'Midfielder', jerseyNumber: 10 },
    { id: 11, name: 'Gianluigi Donnarumma', yearBorn: 1999, team: 'Paris Saint-Germain', position: 'Goalkeeper', jerseyNumber: 99 },
    { id: 12, name: 'Federico Valverde', yearBorn: 1998, team: 'Real Madrid CF', position: 'Midfielder', jerseyNumber: 15 },
    { id: 13, name: 'Son Heung-min', yearBorn: 1992, team: 'Tottenham Hotspur', position: 'Forward', jerseyNumber: 7 },
    { id: 14, name: 'Robert Lewandowski', yearBorn: 1988, team: 'FC Barcelona', position: 'Forward', jerseyNumber: 9 },
    { id: 15, name: 'Alisson Becker', yearBorn: 1992, team: 'Liverpool FC', position: 'Goalkeeper', jerseyNumber: 1 },
];

interface SearchFilters {
    searchText: string;
    yearBornFilter: string;
    teamFilter: string;
}

function PlayerSearch() {
    const [searchText, setSearchText] = useState('');
    const [yearBornFilter, setYearBornFilter] = useState('');
    const [teamFilter, setTeamFilter] = useState('');
    const [columnVisibilityModel, setColumnVisibilityModel] = useState<{ [key: string]: boolean }>({
        id: false,
        name: true,
        yearBorn: true,
        team: true,
        position: true,
        jerseyNumber: true,
    });
    const [savedSearches, setSavedSearches] = useState<string[]>([]);
    const [filtersAnchorEl, setFiltersAnchorEl] = useState<null | HTMLElement>(null);
    const [columnsAnchorEl, setColumnsAnchorEl] = useState<null | HTMLElement>(null);

    const isFiltersMenuOpen = Boolean(filtersAnchorEl);
    const isColumnsMenuOpen = Boolean(columnsAnchorEl);

    const allYears = useMemo(() => [...new Set(initialPlayers.map(player => player.yearBorn))].sort((a, b) => a - b), []);
    const allTeams = useMemo(() => [...new Set(initialPlayers.map(player => player.team))].sort(), []);

    const filteredPlayers = useMemo(() => {
        return initialPlayers.filter(player => {
            const searchMatch = searchText
                ? player.name.toLowerCase().includes(searchText.toLowerCase()) ||
                player.team.toLowerCase().includes(searchText.toLowerCase()) ||
                player.position.toLowerCase().includes(searchText.toLowerCase()) ||
                player.jerseyNumber.toString().includes(searchText)
                : true;
            const yearMatch = yearBornFilter ? player.yearBorn.toString() === yearBornFilter : true;
            const teamMatch = teamFilter ? player.team === teamFilter : true;
            return searchMatch && yearMatch && teamMatch;
        });
    }, [searchText, yearBornFilter, teamFilter]);

    const columns: GridColDef[] = [
        { field: 'name', headerName: 'Name', flex: 1 },
        { field: 'yearBorn', headerName: 'Year Born', width: 100, filterable: false },
        { field: 'position', headerName: 'Position', width: 120 },
        { field: 'jerseyNumber', headerName: 'Jersey #', width: 90 },
        { field: 'team', headerName: 'Team', flex: 1, filterable: false },
        // Add more columns as needed
    ];

    const visibleColumns = useMemo(() => {
        return columns.filter(col => columnVisibilityModel[col.field]);
    }, [columns, columnVisibilityModel]);

    const handleSaveSearch = () => {
        const currentFilters: SearchFilters = { searchText, yearBornFilter, teamFilter };
        const searchString = JSON.stringify(currentFilters);
        if (!savedSearches.includes(searchString)) {
            setSavedSearches([...savedSearches, searchString]);
        }
    };

    const handleLoadSearch = (search: string) => {
        const filters: SearchFilters = JSON.parse(search);
        setSearchText(filters.searchText || '');
        setYearBornFilter(filters.yearBornFilter || '');
        setTeamFilter(filters.teamFilter || '');
    };

    const handleClearFilters = () => {
        setSearchText('');
        setYearBornFilter('');
        setTeamFilter('');
    };

    const handleColumnVisibilityChange = (field: string) => {
        setColumnVisibilityModel(prev => ({
            ...prev,
            [field]: !prev[field],
        }));
    };

    const handleOpenFiltersMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
        setFiltersAnchorEl(event.currentTarget);
    };

    const handleCloseFiltersMenu = () => {
        setFiltersAnchorEl(null);
    };

    const handleOpenColumnsMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
        setColumnsAnchorEl(event.currentTarget);
    };

    const handleCloseColumnsMenu = () => {
        setColumnsAnchorEl(null);
    };

    return (
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2, p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="h6">Player Search</Typography>
                <Button variant="contained" color="primary">
                    Create Profile
                </Button>
            </Box>

            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <Button
                    startIcon={<SaveIcon />}
                    size="small"
                    onClick={handleSaveSearch}
                    variant="outlined"
                >
                    Save Search
                </Button>
                {savedSearches.length > 0 && (
                    <>
                        <FormControl size="small">
                            <InputLabel id="saved-searches-label">Saved Searches</InputLabel>
                            <Select
                                labelId="saved-searches-label"
                                id="saved-searches"
                                value="" // You might want to manage a selected saved search
                                label="Saved Searches"
                            >
                                {savedSearches.map((search, index) => (
                                    <MenuItem key={index} value={search} onClick={() => handleLoadSearch(search)}>
                                        Search {index + 1}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </>
                )}
            </Stack>

            <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                <Button
                    startIcon={<FilterListIcon />}
                    onClick={handleOpenFiltersMenu}
                    size="small"
                    variant="outlined"
                >
                    Filters
                </Button>
                <Button
                    startIcon={<ViewColumnIcon />}
                    onClick={handleOpenColumnsMenu}
                    size="small"
                    variant="outlined"
                >
                    Columns
                </Button>
                <TextField
                    label="Search name, team, position, #"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    size="small"
                    sx={{ flexGrow: 1 }}
                />
                <Button onClick={handleClearFilters} size="small">
                    Clear Filters
                </Button>
            </Stack>

            <Menu
                anchorEl={filtersAnchorEl}
                open={isFiltersMenuOpen}
                onClose={handleCloseFiltersMenu}
                PaperProps={{
                    style: {
                        width: 250,
                    },
                }}
            >
                <ListSubheader>Filter By</ListSubheader>
                <Divider />
                <MenuItem>
                    <FormControl fullWidth size="small">
                        <InputLabel id="year-born-filter-label">Year Born</InputLabel>
                        <Select
                            labelId="year-born-filter-label"
                            id="year-born-filter"
                            value={yearBornFilter}
                            label="Year Born"
                            onChange={(e) => setYearBornFilter(e.target.value)}
                        >
                            <MenuItem value="">All</MenuItem>
                            {allYears.map((year) => (
                                <MenuItem key={year} value={year}>{year}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </MenuItem>
                <MenuItem>
                    <FormControl fullWidth size="small">
                        <InputLabel id="team-filter-label">Team Name</InputLabel>
                        <Select
                            labelId="team-filter-label"
                            id="team-filter"
                            value={teamFilter}
                            label="Team Name"
                            onChange={(e) => setTeamFilter(e.target.value)}
                        >
                            <MenuItem value="">All</MenuItem>
                            {allTeams.map((team) => (
                                <MenuItem key={team} value={team}>{team}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </MenuItem>
                {/* Add more filter options here if needed */}
            </Menu>

            <Menu
                anchorEl={columnsAnchorEl}
                open={isColumnsMenuOpen}
                onClose={handleCloseColumnsMenu}
                PaperProps={{
                    style: {
                        width: 200,
                    },
                }}
            >
                <ListSubheader>Show Columns</ListSubheader>
                <Divider />
                {columns.map((col) => (
                    <MenuItemMUI key={col.field} onClick={() => handleColumnVisibilityChange(col.field)}>
                        <ListItemIcon>
                            <Checkbox
                                checked={columnVisibilityModel[col.field] || false}
                                edge="start"
                                tabIndex={-1}
                                disableRipple
                            />
                        </ListItemIcon>
                        <ListItemText primary={col.headerName} />
                    </MenuItemMUI>
                ))}
            </Menu>

            <div style={{ height: 400, width: '100%' }}>
                <DataGrid
                    rows={filteredPlayers}
                    columns={visibleColumns}
                    pageSizeOptions={[5, 10, 20]}
                    disableRowSelectionOnClick
                />
            </div>
        </Box>
    );
}

export default PlayerSearch;